// The built-in backstop patterns (E01-S2-T8, D-86, D-100). Each one is anchored with a negative
// lookbehind on its first character class, so a long run of digits or letters is tried from its start
// only and the scan stays linear. Digit groups may carry one separator between them — a space, dot,
// dash, slash, underscore or colon, or a run of up to eight whitespace characters (D-100); a longer
// run of digits, or another group after a separator, is not a match of the shorter format. Every
// quantifier is bounded. They are not held to the pack subset (they use lookbehind and a
// backreference): the test suite vets them with a reference matcher that counts its steps on
// worst-case inputs, and compilePatterns trusts them at run time, by id and source, without a probe.
// The atlas and tenant policy add their own patterns through compilePatterns, in the pack subset.
// one separator between digit groups (D-100: / _ : and whitespace runs join space, dot and dash)
const S = "(?:[./_:-]|\\s{1,8})";
// not preceded by a digit, nor by a digit and a separator; not followed by either
const B = `(?<![0-9])(?<![0-9]${S})`;
const E = `(?![0-9])(?!${S}[0-9])`;
// The digits-run rule's separators: the punctuation ones only, not whitespace. Text the firewall and
// the audit record read raw is prose, where "006 2023.01" or "Q3 2026 12" are separate numbers; at
// the edge, perception also scans every value with its whitespace removed (scanValue), so there
// "555 123 4567" is one run all the same.
const P = "[./_:-]";
const PB = `(?<![0-9])(?<![0-9]${P})`;
const PE = `(?![0-9])(?!${P}[0-9])`;
// A calendar date as a screen prints one — MM/DD/YYYY, DD.MM.YYYY or YYYY-MM-DD, years 1900 to 2099,
// one separator kind or another — standing alone as its digit run. The digits-run rule leaves it,
// and only it: a date joined to more digits by punctuation is still a run.
const MONTH = "(?:0?[1-9]|1[0-2])";
const DAY = "(?:0?[1-9]|[12][0-9]|3[01])";
const YEAR = "(?:19|20)[0-9]{2}";
const DATE = `(?:${MONTH}[./-]${DAY}[./-]${YEAR}|${DAY}[./-]${MONTH}[./-]${YEAR}|${YEAR}[./-]${MONTH}[./-]${DAY})`;
// A time of day (T0, 3 Nov 2026): HH:MM, HH:MM:SS or HH:MM:SS.fff with one to six fraction digits,
// then optionally Z or an offset — alone, or after a date joined by T, whitespace or (where the edge
// has removed the whitespace) nothing. Like the date, it is left only when it is the whole run, so
// "12:34:567", "123:4567" or a time with more digits after it is still a run.
const TIME = "(?:[01]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9](?:[.][0-9]{1,6})?)?";
const ZONE = "(?:z|[+-](?:[01][0-9]|2[0-3]):?[0-5][0-9])?";
const STAMP = `(?:${DATE}|(?:${DATE}(?:t|\\s{1,8})?)?${TIME}${ZONE})${PE}`;
export const BUILTIN_PATTERNS = Object.freeze([
    // a 16-digit card-like run, in fours with optional separators: 4111 1111 1111 1111
    { id: "card-number", regex: `${B}[0-9]{4}(?:${S}?[0-9]{4}){3}${E}` },
    // US tax ids: a Social Security number NNN-NN-NNNN (the same separator, or none, both times) or an
    // Employer Identification Number NN-NNNNNNN
    { id: "us-tax-id", regex: `${B}(?:[0-9]{3}(${S}?)[0-9]{2}\\1[0-9]{4}|[0-9]{2}${S}[0-9]{7})${E}` },
    // brokerage account numbers: NNN-NNNNN, or eight digits, bare or split in fours (1234-5678)
    { id: "account-number", regex: `${B}(?:[0-9]{3}${S}[0-9]{5}|[0-9]{4}${S}?[0-9]{4})${E}` },
    // an e-mail address: local part at most 64 characters (RFC 5321), at most five domain labels
    {
        id: "email",
        regex: "(?<![a-z0-9._%+-])[a-z0-9._%+-]{1,64}@[a-z0-9-]{1,63}(?:\\.[a-z0-9-]{1,63}){0,3}\\.[a-z]{2,24}(?![a-z])",
    },
    // D-100: seven or more digits in one run, ignoring the punctuation between them — 123/456/789,
    // 12_34_567, 12:34:567, 1234567 — whatever format they are in (see P for whitespace). Last, so a
    // value the formats above know is reported under their id. A calendar date, a time of day, or the
    // two together, standing alone as the run, is not one (see DATE, TIME).
    { id: "digits-run", regex: `${PB}(?!${STAMP})[0-9](?:${P}?[0-9]){6}` },
]);
/**
 * Code-owned trusted patterns (T0, 3 Nov 2026): written in this repository's code for one of its own
 * services, not supplied by a pack, so held to the same CI vetting as the built-ins (the reference
 * step counter and the worst-case windows) instead of the pack subset, and trusted by compilePatterns
 * only on a match by id AND source. A service adds them to the built-ins itself; they are not part
 * of BUILTIN_PATTERNS, so perception and the firewall do not run them.
 */
export const CODE_OWNED_PATTERNS = Object.freeze([
    // apps/foundry's sandbox intake (INTAKE_PATTERNS): money, marked with a currency or written with
    // thousands separators and cents. A count without separators ("about 1200 rows") is not an amount.
    {
        id: "amount-currency",
        regex: "(?<![A-Za-z0-9])(?:[$€£¥]|USD|EUR|GBP|CAD|CHF|JPY) ?[0-9][0-9,]{0,17}(?:[.][0-9]{1,2})?(?![0-9])",
    },
    { id: "amount-grouped", regex: "(?<![0-9.,])[0-9]{1,3}(?:,[0-9]{3}){1,5}[.][0-9]{2}(?![0-9])" },
]);
//# sourceMappingURL=patterns.js.map